/**
 * Multi-step Flows panel (feature v6-flows-and-fx, FR-TS-127). Polls the simulator's own
 * /flows and renders each in-progress payment lifecycle (cross-border, SEPA Direct Debit,
 * SWIFT, P2P) as a card with a step tracker that fills in as the flow advances over time.
 * This is the illustrative lifecycle view — single-shot real calls stay in the Live feed.
 */

import { pinFlow, refreshFlows, selectedFlowId } from './inspector.js';

interface FlowStep {
  name: string;
  status: string; // pending | active | done | failed | blocked
  detail?: string;
  msgType?: string; // ISO 20022 message type, e.g. "pacs.008"
  /** Correlation ID of the real call that advanced this step — look it up in the bank's audit. */
  correlationId?: string;
}

export interface Flow {
  /** The BACKEND's id for this lifecycle (p2p requestId, sdd collectionId, …), not a local counter. */
  id: string;
  kind: string; // cross_border | sdd | swift | p2p
  title: string;
  bank: string;
  steps: FlowStep[];
  status: string; // in_progress | settled | failed | awaiting_external
  ageSeconds: number;
}

/**
 * TODO #8 (task 4) — one bank's current SWIFT observation state, from `GET /swift-observation`
 * (live-only; simulated mode reports an empty array). Mirrors `SwiftObservationState` in
 * src/domain/feed.ts: unknown | disabled | not_configured | error | no_messages | observed.
 */
export interface SwiftObservation {
  bank: string;
  state: string;
  detail: string;
}

const KIND_LABEL: Record<string, string> = {
  cross_border: 'Cross-border',
  sdd: 'Direct debit',
  swift: 'SWIFT',
  p2p: 'P2P',
  sct: 'Cross-bank SCT',
};

// `blocked` = the rail really has this step, but no endpoint we hold can observe it (e.g. a SEPA
// DD settling on D-day via an external CSM). It is drawn as a wall, never as a completed tick.
const STEP_GLYPH: Record<string, string> = { done: '●', active: '◐', pending: '○', failed: '✕', blocked: '⊘' };
const MAX_CARDS = 10;

/** Track previous render state per flow id to detect transitions for animations. */
interface FlowSnapshot { status: string; stepStatuses: string[] }
const prevState = new Map<string, FlowSnapshot>();

function span(cls: string, text: string): HTMLSpanElement {
  const s = document.createElement('span');
  s.className = cls;
  s.textContent = text;
  return s;
}

export function renderFlows(doc: Document, flows: Flow[]): void {
  const list = doc.querySelector('[data-flows]');
  if (!list) return;
  const empty = doc.querySelector('[data-flows-empty]');
  if (empty) (empty as HTMLElement).style.display = flows.length === 0 ? 'block' : 'none';

  // Prune stale entries from prevState for flows that scrolled off
  const visibleIds = new Set(flows.slice(0, MAX_CARDS).map((f) => f.id));
  for (const id of prevState.keys()) { if (!visibleIds.has(id)) prevState.delete(id); }

  list.replaceChildren(
    ...flows.slice(0, MAX_CARDS).map((f) => {
      const prev = prevState.get(f.id);
      const justSettled = prev?.status === 'in_progress' && f.status === 'settled';
      const justFailed = prev?.status === 'in_progress' && f.status === 'failed';

      const card = doc.createElement('li');
      card.className = 'flow';
      card.setAttribute('data-kind', f.kind);
      card.setAttribute('data-status', f.status);
      card.setAttribute('data-flow-id', String(f.id));
      if (f.id === selectedFlowId()) card.setAttribute('data-selected', 'true');
      if (justSettled) card.setAttribute('data-just-settled', 'true');
      if (justFailed) card.setAttribute('data-just-failed', 'true');
      card.addEventListener('click', () => {
        pinFlow(doc, f); // pin into the shared inspector (FR-TS-129)
        list.querySelectorAll('.flow').forEach((c) => {
          if (c.getAttribute('data-flow-id') === String(f.id)) c.setAttribute('data-selected', 'true');
          else c.removeAttribute('data-selected');
        });
      });

      const head = doc.createElement('div');
      head.className = 'flow-head';
      head.append(
        span(`flow-kind k-${f.kind}`, KIND_LABEL[f.kind] ?? f.kind),
        span('flow-title', f.title),
        span(`flow-state s-${f.status}`, f.status === 'settled' ? 'settled' : f.status === 'failed' ? 'failed' : 'in progress'),
      );
      card.append(head);

      const track = doc.createElement('div');
      track.className = 'flow-track';
      f.steps.forEach((st, i) => {
        if (i > 0) {
          const conn = span('flow-conn', '─');
          // Animate the connector filling in when the step at this index just became active
          const wasActive = prev?.stepStatuses[i] !== 'active' && st.status === 'active';
          if (wasActive) conn.setAttribute('data-just-filled', 'true');
          track.append(conn);
        }
        const step = doc.createElement('span');
        step.className = 'flow-step';
        step.setAttribute('data-step', st.status);
        step.append(span('flow-glyph', STEP_GLYPH[st.status] ?? '○'), span('flow-name', st.name));
        if (st.msgType) step.append(span('flow-msg', st.msgType));
        if (st.detail) step.title = st.detail;
        track.append(step);
      });
      card.append(track);

      // active step's detail (e.g. "awaiting D-day settlement")
      const active = f.steps.find((s) => s.status === 'active');
      if (active?.detail) card.append(span('flow-detail', active.detail));

      // Record this render state for next tick's transition detection
      prevState.set(f.id, { status: f.status, stepStatuses: f.steps.map((s) => s.status) });
      return card;
    }),
  );
}

/**
 * TODO #8 (task 4) — when there's no SWIFT card to show (gateway dormant, disabled, or the run
 * hasn't produced any outbound MX yet), the panel used to just look empty — no way to tell
 * "nothing generated yet" apart from a real auth/network error. Renders one line per bank's
 * observed state instead, but ONLY while no `swift`-kind card is on screen: once a real SWIFT
 * flow appears, that card IS the evidence, so this note steps out of the way rather than
 * duplicating it.
 */
export function renderSwiftObservation(doc: Document, flows: Flow[], observations: SwiftObservation[]): void {
  const el = doc.querySelector('[data-swift-observation]');
  if (!el) return;
  const hasSwiftCards = flows.some((f) => f.kind === 'swift');
  if (hasSwiftCards || observations.length === 0) {
    (el as HTMLElement).style.display = 'none';
    el.replaceChildren();
    return;
  }
  (el as HTMLElement).style.display = 'block';
  el.replaceChildren(
    ...observations.map((o) => {
      const row = doc.createElement('div');
      row.className = 'swift-obs-row';
      row.setAttribute('data-swift-state', o.state);
      row.append(
        span('swift-obs-label', 'SWIFT'),
        span('swift-obs-bank', o.bank),
        span('swift-obs-state', o.state.replace(/_/g, ' ')),
        span('swift-obs-detail', o.detail),
      );
      return row;
    }),
  );
}

async function getJson<T>(url: string): Promise<T | null> {
  try {
    const r = await fetch(url, { cache: 'no-store' });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

export function initFlows(doc: Document = document, pollMs = 1200): void {
  const tick = async (): Promise<void> => {
    const [data, swift] = await Promise.all([
      getJson<{ flows: Flow[] }>('/flows'),
      getJson<{ banks: SwiftObservation[] }>('/swift-observation'),
    ]);
    if (data) {
      const flows = data.flows ?? [];
      renderFlows(doc, flows);
      refreshFlows(doc, flows); // live-update a pinned flow as it advances
      renderSwiftObservation(doc, flows, swift?.banks ?? []);
    }
  };
  void tick();
  window.setInterval(() => void tick(), pollMs);
}
